import {createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";
import {MapEntity} from "./mapTypes.d";
import {useDispatch, useSelector} from 'react-redux'
import type {RootState, AppDispatch} from '../../store';
import * as orbatService from '../../services/orbatService';
import {formatDtg, toUtcEpochMs} from '../../utils/parseTimestamp';

interface MapState {
    value: MapEntity[]
    isLoading: boolean
}

const initialState: MapState = {
    value: [],
    isLoading: false,
}

/**
 * Fetch units for the selected faction from the REST API and convert
 * them to MapEntity objects. Units without position data are included
 * in the store so MQTT position updates can merge into them later.
 */
export const fetchMapUnits = createAsyncThunk<
    MapEntity[],
    { eventId: string; factionId: string }
>('mapEntities/fetchUnits', async ({ eventId, factionId }) => {
    const response = await orbatService.getUnits(eventId);
    return response.units
        .filter((u) => u.faction_id === factionId)
        .map((unit) => {
            const lastUpdateMs = toUtcEpochMs(unit.position_time);
            return {
                id: unit.id,
                latLng: unit.latitude && unit.longitude
                    ? { lat: unit.latitude, lng: unit.longitude }
                    : { lat: 0, lng: 0 },
                code: unit.sidc,
                own: false,
                // `name` is carried as its own field, not folded into
                // markerProperties as a `uniqueDesignation` modifier. The map
                // layer derives that label when it draws the symbol, which
                // keeps the identity out of the persisted/wire-encoded
                // modifier map. See MapEntity.name.
                name: unit.name,
                markerProperties: {
                    ...(unit.text_modifiers ?? {}),
                    ...(unit.position_time ? { dtg: formatDtg(unit.position_time) } : {}),
                },
                lastUpdateMs,
            };
        });
});


export const mapSlice = createSlice({
    name: 'mapEntities',
    initialState: initialState,
    reducers: {
        update: (state, action) => {
            const idx = state.value.findIndex((m) => m.id === action.payload.id);
            if (idx !== -1) {
                state.value[idx] = action.payload;
            }
        },
        addEntity: (state, action: PayloadAction<MapEntity>) => {
            const idx = state.value.findIndex((m) => m.id === action.payload.id);

            if (idx === -1) {
                state.value.push(action.payload);
            } else {
                // Merge: preserve existing properties not present in the update
                const existing = state.value[idx];
                state.value[idx] = {
                    ...existing,
                    ...action.payload,
                    markerProperties: {
                        ...existing.markerProperties,
                        ...action.payload.markerProperties,
                    },
                };
            }
        },
        removeEntity: (state, action: PayloadAction<string>) => {
            state.value = state.value.filter((m) => m.id !== action.payload);
        },
        clearEntities: (state) => {
            state.value = [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMapUnits.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchMapUnits.fulfilled, (state, action) => {
                state.isLoading = false;
                state.value = action.payload;
            })
            .addCase(fetchMapUnits.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const {addEntity, removeEntity, clearEntities} = mapSlice.actions

export default mapSlice.reducer
// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()